import { Modal } from '@mantine/core'
import { useMedplum } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  IconAlertTriangle,
  IconCalendarEvent,
  IconCircleCheck,
  IconCopy,
  IconFileText,
  IconMessage,
  IconSend,
  IconSparkles,
} from '@tabler/icons-react'
import { downloadAttachment } from '@/utils/downloadAttachment.ts'
import { logError } from '@/utils/errorUtils'
import {
  CardBadge,
  CardSkeleton,
  DetailCard,
  type BadgeTone,
} from '@/components/ui/detailCardPrimitives'
import type { KateCheckIn, KateCheckInState } from '../hooks/useKateCheckIn'

/** What the call settled, said the way a coordinator reads it at a glance. */
const OUTCOME: Record<
  NonNullable<KateCheckIn['outcome']>,
  { label: string; tone: BadgeTone } | undefined
> = {
  confirmed: { label: 'Appointment confirmed', tone: 'green' },
  rescheduled: { label: 'Rescheduled by Kate', tone: 'blue' },
  // A missed visit is not moved: Kate books a replacement beside it.
  rebooked: { label: 'Rebooked by Kate', tone: 'blue' },
  cancelled: { label: "Cancelled at the patient's request", tone: 'gray' },
  declined: { label: 'Patient declined another appointment', tone: 'gray' },
  needs_assistance: { label: 'Needs assistance', tone: 'coral' },
  // Nothing about the appointment changed; the rows say what was captured.
  unchanged: undefined,
}

const STATUS_BADGE: Record<KateCheckIn['status'], { label: string; tone: BadgeTone }> = {
  not_sent: { label: 'Not sent', tone: 'gray' },
  sent: { label: 'Awaiting response', tone: 'blue' },
  opened: { label: 'Opened', tone: 'blue' },
  in_progress: { label: 'In progress', tone: 'blue' },
  completed: { label: 'Completed', tone: 'green' },
  reviewed: { label: 'Reviewed', tone: 'green' },
  failed: { label: "Couldn't finish", tone: 'coral' },
}

/** What the card says while it has no answers to show. */
const PENDING_COPY: Partial<Record<KateCheckIn['status'], { title: string; detail: string }>> = {
  sent: {
    title: 'No responses yet',
    detail: 'The patient has not started or completed the Kate check-in.',
  },
  opened: {
    title: 'No responses yet',
    detail: 'The patient opened the link but has not started the check-in.',
  },
  in_progress: {
    title: 'Kate is talking with the patient',
    detail: 'The check-in is under way right now.',
  },
  failed: {
    title: "Kate couldn't finish this check-in",
    detail: 'A follow-up task went to the care team.',
  },
}

const VIA_LABEL = { sms: 'SMS', email: 'email', link: 'copied link' } as const

const when = (iso?: string): string =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''

const stop = (e: React.MouseEvent) => e.stopPropagation()

const PrimaryButton = ({
  label,
  onClick,
  disabled,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation()
      onClick()
    }}
    className="w-full rounded-lg bg-[#2C5FE0] px-3 py-2 text-[13px] font-bold text-white hover:bg-[#2451C4] disabled:cursor-default disabled:bg-[#F1F3F5] disabled:text-[#9AA0A8]"
  >
    {label}
  </button>
)

/** The one line that says what the call settled, above the answers it settled it with. */
const OutcomeStrip = ({
  outcome,
  from,
  to,
}: {
  outcome: { label: string; tone: BadgeTone }
  from?: string
  to?: string
}) => (
  <div className="flex flex-col gap-1 rounded-lg bg-[#F7F8FA] px-2.5 py-2">
    <CardBadge label={outcome.label} tone={outcome.tone} />
    {to ? (
      <div className="text-[12.5px] text-[#4B5563]">
        {from ? <span className="line-through">{formatWhen(from)}</span> : null}
        {from ? ' → ' : ''}
        <span className="font-semibold text-[#1F2733]">{to}</span>
      </div>
    ) : null}
  </div>
)

type RebookedVisit = NonNullable<NonNullable<KateCheckIn['rebooking']>['appointment']>

/** The follow-up's badge names the delivery step; the check-in's "Awaiting response" is a step later. */
const FOLLOW_UP_BADGE: Partial<Record<KateCheckIn['status'], { label: string; tone: BadgeTone }>> =
  {
    sent: { label: 'Sent', tone: 'blue' },
    // A call that ended with neither a booking nor a "no" is not done.
    completed: { label: 'Needs follow-up', tone: 'coral' },
  }

/** What the follow-up card says while the invite is out and nothing has come of it yet. */
const FOLLOW_UP_PENDING: Partial<Record<KateCheckIn['status'], string>> = {
  sent: 'Kate will offer available times when the patient opens the link.',
  opened: 'The patient opened the link but has not started the call.',
  in_progress: 'Kate is talking with the patient right now.',
}

/**
 * The new time arrives already spoken, in the clinic's zone; the old one is a
 * raw instant, so it is read in the same zone rather than the viewer's.
 */
const CLINIC_TIME_ZONE = 'America/New_York'
const formatWhen = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('en-US', {
    timeZone: CLINIC_TIME_ZONE,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
const clinicDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    timeZone: CLINIC_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
const clinicTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString('en-US', {
    timeZone: CLINIC_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
  })

const OutlineButton = ({
  icon,
  label,
  onClick,
  disabled,
  accent,
}: {
  icon?: React.ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
  accent?: boolean
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation()
      onClick()
    }}
    className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-[13px] font-bold whitespace-nowrap hover:bg-[#F7F8FA] disabled:cursor-default disabled:opacity-60 ${accent ? 'border-[#2C5FE0] text-[#2C5FE0]' : 'border-[#E7E9EC] text-[#33383F]'}`}
  >
    {icon}
    {label}
  </button>
)

const ResponseRows = ({ rows }: { rows: KateCheckIn['rows'] }) => (
  <ul className="flex flex-col gap-1.5">
    {rows.map((row) => (
      <li
        key={row.key}
        className="flex items-start gap-2 rounded-lg border border-[#E7E9EC] px-2.5 py-2 text-[12.5px] text-[#1F2733]"
      >
        {row.tone === 'attention' ? (
          <IconAlertTriangle size={16} stroke={2} className="mt-px shrink-0 text-[#D97706]" />
        ) : (
          <IconCircleCheck size={16} stroke={2} className="mt-px shrink-0 text-[#1A8245]" />
        )}
        <span className="min-w-0">{row.label}</span>
      </li>
    ))}
  </ul>
)

const NewAppointmentBlock = ({
  visit,
  booked,
  providerName,
}: {
  visit?: RebookedVisit
  booked: boolean
  providerName?: string
}) => (
  <div
    className={`rounded-lg px-2.5 py-2 ${visit ? 'border border-[#BFD3FF] bg-[#F3F7FF]' : 'bg-[#F7F8FA]'}`}
  >
    <div className="flex items-center gap-1.5 text-[10px] font-semibold text-[#2C5FE0] uppercase">
      <IconCalendarEvent size={14} stroke={2} />
      New appointment
    </div>
    {visit ? (
      <>
        <div className="mt-1 text-[15px] font-bold text-[#1F2733]">{clinicDate(visit.start)}</div>
        <div className="text-[12.5px] text-[#4B5563]">
          {clinicTime(visit.start)}
          {visit.end ? ` – ${clinicTime(visit.end)}` : ''}
        </div>
        {providerName ? (
          <div className="mt-1 text-[12.5px] text-[#4B5563]">
            Provider <span className="font-semibold text-[#1F2733]">{providerName}</span>
          </div>
        ) : null}
        <div className="mt-1 flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1A8245]">
          <IconCircleCheck size={15} stroke={2} />
          Confirmed by patient
        </div>
      </>
    ) : (
      <div className="mt-1 text-[12.5px] text-[#6B7280]">
        {booked
          ? 'Kate booked a new appointment. Find it on the schedule.'
          : 'Appointment details will appear here once confirmed.'}
      </div>
    )}
  </div>
)

/**
 * "Kate no-show follow-up" — the card of a missed visit. It tracks the invite and
 * the call it opens apart from any pre-visit check-in the same visit had, and
 * ends with the replacement visit Kate booked or the patient's "no".
 */
const NoShowFollowUpCard = ({
  checkIn,
  rebooking,
  providerName,
  onViewAppointment,
}: {
  checkIn: KateCheckInState
  rebooking: NonNullable<KateCheckIn['rebooking']>
  providerName?: string
  onViewAppointment?: (visit: RebookedVisit) => void
}) => {
  const navigate = useNavigate()
  const { data, busy } = checkIn
  const settled = Boolean(rebooking.bookedAt || rebooking.declinedAt)
  const status = rebooking.status
  const badge = rebooking.bookedAt
    ? STATUS_BADGE.completed
    : rebooking.declinedAt
      ? { label: 'Declined', tone: 'gray' as const }
      : (FOLLOW_UP_BADGE[status] ?? STATUS_BADGE[status])
  const visit = rebooking.appointment
  // The invite is stamped as sent even when no channel took it; the no-show card
  // has no "Copy link", so a link-only send here is a delivery that failed.
  const undelivered = status !== 'not_sent' && data?.sentVia === 'link'
  const sendLabel =
    busy === 'send'
      ? 'Sending…'
      : rebooking.invitedAt
        ? 'Resend Kate follow-up'
        : 'Send Kate follow-up'
  const send = () => void checkIn.send('rebooking')

  return (
    <DetailCard
      icon={<IconSparkles size={17} stroke={2} />}
      title="Kate no-show follow-up"
      subtitle={
        rebooking.invitedAt
          ? `Contacted ${clinicDate(rebooking.invitedAt)} · ${clinicTime(rebooking.invitedAt)}`
          : 'Help the patient reschedule'
      }
      badge={<CardBadge label={badge.label} tone={badge.tone} />}
    >
      <div className="flex flex-col gap-2.5">
        {rebooking.bookedAt ? (
          <>
            <div className="flex items-center gap-2 rounded-lg bg-[#E8F6EE] px-2.5 py-2 text-[13px] font-bold text-[#1A8245]">
              <IconCircleCheck size={16} stroke={2} className="shrink-0" />
              Patient rescheduled
            </div>
            <div className="text-[12.5px] text-[#6B7280]">
              Kate offered available times and the patient confirmed a new appointment.
            </div>
          </>
        ) : rebooking.declinedAt ? (
          <>
            <CardBadge label="Patient declined another appointment" tone="gray" />
            <div className="text-[12.5px] text-[#6B7280]">
              The patient said they did not want another appointment. The care team can reach out
              when they are ready.
            </div>
          </>
        ) : status === 'failed' ? (
          <div className="flex items-center gap-2 rounded-lg bg-[#FDECE8] px-2.5 py-2 text-[12.5px] font-semibold text-[#E5533D]">
            <IconAlertTriangle size={15} stroke={2} className="shrink-0" />
            Kate couldn&apos;t finish the follow-up call. A follow-up task went to the care team.
          </div>
        ) : status === 'completed' ? (
          <div className="flex items-center gap-2 rounded-lg border border-[#F5D89A] bg-[#FFF8E6] px-2.5 py-2 text-[12.5px] font-semibold text-[#8A5A00]">
            <IconAlertTriangle size={15} stroke={2} className="shrink-0" />
            The call ended without a new time. The care team will follow up.
          </div>
        ) : (
          <div className="text-[12.5px] text-[#6B7280]">
            {status === 'not_sent'
              ? 'Kate offers available appointment times and helps the patient reschedule.'
              : undelivered
                ? 'The invite could not be delivered — the patient has no phone or email on file.'
                : FOLLOW_UP_PENDING[status]}
          </div>
        )}
        {settled ? null : status === 'not_sent' ? (
          <>
            <PrimaryButton label={sendLabel} disabled={busy !== null || !data} onClick={send} />
            <div className="text-center text-[11px] text-[#9AA0A8]">No follow-up sent yet.</div>
          </>
        ) : (
          <OutlineButton label={sendLabel} accent disabled={busy !== null} onClick={send} />
        )}
        {data?.followUpTask ? (
          <OutlineButton
            label="View task"
            onClick={() =>
              navigate(
                `/appointments?tab=care-queue&board=${data.followUpTask?.boardId}&item=${data.followUpTask?.itemId}`,
              )
            }
          />
        ) : null}
        <div className="h-px bg-[#EEF0F2]" />
        <NewAppointmentBlock
          visit={visit}
          booked={Boolean(rebooking.bookedAt)}
          providerName={visit?.providerName ?? providerName}
        />
        {visit?.encounterId && onViewAppointment ? (
          <PrimaryButton label="View new appointment" onClick={() => onViewAppointment(visit)} />
        ) : null}
      </div>
    </DetailCard>
  )
}

/**
 * "Kate pre-visit check-in" card of the appointment preview: status badge, the
 * sent/completed timestamps, the filed answers, and the staff actions. Everything
 * shown is what `kateCheckInGet` returned; the review modal reuses the same data.
 */
export const KateCheckInCard = ({
  checkIn,
  providerName,
  onViewAppointment,
}: {
  checkIn: KateCheckInState
  /** The visit's provider, for the replacement block when the booking carries no display name. */
  providerName?: string
  onViewAppointment?: (visit: RebookedVisit) => void
}) => {
  const medplum = useMedplum()
  const navigate = useNavigate()
  const [reviewOpen, setReviewOpen] = useState(false)
  const [openingTranscript, setOpeningTranscript] = useState(false)
  const { data, loading, loadFailed, busy } = checkIn
  // A missed visit has a follow-up to run, not a check-in: its own card.
  if (data?.rebooking) {
    return (
      <NoShowFollowUpCard
        checkIn={checkIn}
        rebooking={data.rebooking}
        providerName={providerName}
        onViewAppointment={onViewAppointment}
      />
    )
  }
  const outcome = data?.outcome ? OUTCOME[data.outcome] : undefined
  // With nothing loaded there is no status to show: a badge and subtitle derived
  // from a default would assert "Not sent" over a check-in we simply failed to read.
  const unknown = loadFailed && !data
  // A refresh that failed on top of an earlier good read: the rows are still worth
  // showing, but they are as old as the last success and must say so.
  const stale = loadFailed && Boolean(data)
  const status = data?.status ?? 'not_sent'
  const badge = STATUS_BADGE[status]
  const failed = status === 'failed'
  // A later call that failed leaves the earlier call's filed answers on the chart.
  const hasResponses =
    status === 'completed' || status === 'reviewed' || (failed && Boolean(data?.completedAt))
  const pending = PENDING_COPY[status]
  const needsReview = data && status !== 'reviewed' ? data.needsReview : 0
  // A resend after completion re-opens the patient link (PD-1619) while the filed
  // answers stay on the chart, so the card keeps them and says a newer one is due.
  const resent =
    hasResponses &&
    !failed &&
    Boolean(
      data?.sentAt && data.completedAt && Date.parse(data.sentAt) > Date.parse(data.completedAt),
    )

  const openTranscript = async () => {
    const url = data?.transcript?.url
    if (!url) return
    setOpeningTranscript(true)
    try {
      const blob = await downloadAttachment(medplum, url)
      window.open(URL.createObjectURL(blob), '_blank', 'noopener')
    } catch (error) {
      logError('schedule', 'Open Kate transcript', error)
      showNotification({ color: 'red', message: 'Could not open the transcript.' })
    } finally {
      setOpeningTranscript(false)
    }
  }

  const subtitle = data?.sentAt ? (
    <>
      <div>
        {resent ? 'Re-sent' : 'Sent'} {when(data.sentAt)}
        {data.sentVia ? ` via ${VIA_LABEL[data.sentVia]}` : ''}
        {resent ? ' — awaiting new answers' : ''}
      </div>
      {hasResponses && data.completedAt && <div>Completed {when(data.completedAt)}</div>}
      {status === 'reviewed' && data.reviewedAt && (
        <div>
          Reviewed {when(data.reviewedAt)}
          {data.reviewedByName ? ` by ${data.reviewedByName}` : ''}
        </div>
      )}
    </>
  ) : (
    'Voice check-in before the visit'
  )

  return (
    <>
      <DetailCard
        icon={<IconSparkles size={17} stroke={2} />}
        title="Kate pre-visit check-in"
        subtitle={(loading && !data) || unknown ? undefined : subtitle}
        badge={
          (loading && !data) || unknown ? undefined : (
            <CardBadge label={badge.label} tone={badge.tone} />
          )
        }
      >
        {stale ? (
          <div className="mb-2.5 flex items-center justify-between gap-2 rounded-lg border border-[#F5D89A] bg-[#FFF8E6] px-2.5 py-2 text-[12px] font-semibold text-[#8A5A00]">
            <span className="inline-flex items-center gap-1.5">
              <IconAlertTriangle size={14} stroke={2} className="shrink-0" />
              Showing the last update — refreshing failed.
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                void checkIn.refresh()
              }}
              className="shrink-0 rounded-md border border-[#E0C38A] bg-white px-2 py-1 text-[11px] font-bold text-[#8A5A00] hover:bg-[#FFFBF0]"
            >
              Retry
            </button>
          </div>
        ) : null}
        {loading && !data ? (
          <CardSkeleton rows={4} />
        ) : unknown ? (
          // Never fall through to the "not sent" branch on a load failure: it
          // would tell staff a completed check-in was never sent.
          <div className="flex flex-col gap-2.5">
            <div className="text-[12.5px] text-[#6B7280]">We couldn&apos;t load this check-in.</div>
            <PrimaryButton label="Retry" onClick={() => void checkIn.refresh()} />
          </div>
        ) : hasResponses && data ? (
          <div className="flex flex-col gap-2.5">
            {failed ? (
              <div className="flex items-center gap-2 rounded-lg bg-[#FDECE8] px-2.5 py-2 text-[12.5px] font-semibold text-[#E5533D]">
                <IconAlertTriangle size={15} stroke={2} className="shrink-0" />
                Kate couldn&apos;t finish the latest check-in. A follow-up task went to the care
                team.
              </div>
            ) : null}
            {outcome ? (
              <OutcomeStrip outcome={outcome} from={data.rescheduledFrom} to={data.rescheduledTo} />
            ) : null}
            {data.rows.length ? (
              <ResponseRows rows={data.rows} />
            ) : (
              <div className="text-[12.5px] text-[#6B7280]">
                The call ended before Kate captured an answer.
                {data.transcript ? ' The transcript is in the chart.' : ''}
              </div>
            )}
            <PrimaryButton label="Review responses" onClick={() => setReviewOpen(true)} />
            {data.followUpTask ? (
              <OutlineButton
                label="View task"
                onClick={() =>
                  navigate(
                    `/appointments?tab=care-queue&board=${data.followUpTask?.boardId}&item=${data.followUpTask?.itemId}`,
                  )
                }
              />
            ) : null}
            <OutlineButton
              label={busy === 'send' ? 'Sending…' : 'Resend check-in'}
              accent
              disabled={busy !== null}
              onClick={() => void checkIn.send()}
            />
            {needsReview > 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-[#F5D89A] bg-[#FFF8E6] px-2.5 py-2 text-[12.5px] font-semibold text-[#8A5A00]">
                <IconAlertTriangle size={15} stroke={2} className="shrink-0" />
                {needsReview} response{needsReview === 1 ? '' : 's'} need
                {needsReview === 1 ? 's' : ''} review
              </div>
            )}
          </div>
        ) : pending ? (
          <div className="flex flex-col items-center gap-2.5 py-2 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F1F3F7] text-[#6B7280]">
              {failed ? (
                <IconAlertTriangle size={22} stroke={1.8} />
              ) : (
                <IconMessage size={22} stroke={1.8} />
              )}
            </span>
            <div className="text-[14px] font-bold text-[#1F2733]">{pending.title}</div>
            <div className="text-[12px] text-[#6B7280]">{pending.detail}</div>
            <div className="flex w-full gap-2">
              <OutlineButton
                icon={<IconSend size={14} stroke={2} />}
                label={busy === 'send' ? 'Sending…' : 'Resend check-in'}
                accent
                disabled={busy !== null}
                onClick={() => void checkIn.send()}
              />
              <OutlineButton
                icon={<IconCopy size={14} stroke={2} />}
                label={busy === 'copy' ? 'Copying…' : 'Copy link'}
                disabled={busy !== null}
                onClick={() => void checkIn.copyLink()}
              />
            </div>
            {failed && data?.followUpTask ? (
              <OutlineButton
                label="View task"
                onClick={() =>
                  navigate(
                    `/appointments?tab=care-queue&board=${data.followUpTask?.boardId}&item=${data.followUpTask?.itemId}`,
                  )
                }
              />
            ) : null}
            <PrimaryButton label="Review responses" onClick={() => undefined} disabled />
            {!failed && (
              <div className="text-[11px] text-[#9AA0A8]">
                We&apos;ll update this card when the patient responds.
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            <div className="text-[12.5px] text-[#6B7280]">
              Kate confirms the appointment, reschedules or cancels it at the patient&apos;s
              request, and captures the reason for the visit, insurance changes and video readiness
              in a one-minute voice call.
            </div>
            <PrimaryButton
              label={busy === 'send' ? 'Sending…' : 'Send Kate check-in'}
              disabled={busy !== null || !data}
              onClick={() => void checkIn.send()}
            />
          </div>
        )}
      </DetailCard>

      <Modal
        opened={reviewOpen}
        onClose={() => setReviewOpen(false)}
        title="Kate pre-visit check-in"
        centered
        radius="lg"
        size="md"
        // Sit above the preview popover (default z-index 300) that opened it.
        zIndex={400}
      >
        {data && (
          <div className="flex flex-col gap-3" onClick={stop} onMouseDown={stop}>
            <div className="text-[12px] text-[#6B7280]">{subtitle}</div>
            {data.rows.length ? (
              <ResponseRows rows={data.rows} />
            ) : (
              <div className="text-[12.5px] text-[#6B7280]">No answers were captured.</div>
            )}
            {data.summary && (
              <div className="rounded-lg bg-[#F7F8FA] px-3 py-2 text-[12.5px] text-[#1F2733]">
                <div className="mb-1 text-[10px] font-semibold text-[#9AA0A8] uppercase">
                  Kate&apos;s summary
                </div>
                {data.summary}
              </div>
            )}
            <div className="flex gap-2">
              {data.transcript?.url && (
                <OutlineButton
                  icon={<IconFileText size={14} stroke={2} />}
                  label={openingTranscript ? 'Opening…' : 'Open transcript'}
                  disabled={openingTranscript}
                  onClick={() => void openTranscript()}
                />
              )}
              <OutlineButton label="Close" onClick={() => setReviewOpen(false)} />
            </div>
            {status !== 'reviewed' && (
              <PrimaryButton
                label={busy === 'review' ? 'Saving…' : 'Mark as reviewed'}
                disabled={busy !== null}
                onClick={() => {
                  void checkIn.review().then(() => setReviewOpen(false))
                }}
              />
            )}
          </div>
        )}
      </Modal>
    </>
  )
}
